<template>
  <div class="min-h-screen flex flex-col lg:flex-row relative overflow-hidden font-sans">

    <!-- =============================================
         LEFT PANEL — Branding & Animated Background
         ============================================= -->
    <div class="hidden lg:flex lg:w-1/2 relative items-center justify-center bg-primary-500 dark:bg-surface-950 overflow-hidden">

      <!-- Animated gradient orbs -->
      <div class="absolute inset-0 overflow-hidden">
        <div class="orb orb-1"></div>
        <div class="orb orb-2"></div>
        <div class="orb orb-3"></div>
        <div class="orb orb-4"></div>
      </div>

      <!-- Subtle grid overlay -->
      <div class="absolute inset-0 bg-pattern-dots opacity-30"></div>

      <!-- Content -->
      <div class="relative z-10 max-w-md px-8 text-center animate-fade-up">
        <!-- Logo -->
        <div class="flex justify-center mb-8">
          <div class="relative">
            <div class="absolute -inset-4 bg-accent-500/20 rounded-full blur-2xl animate-pulse-soft"></div>
            <img
              src="../../assets/earth.png"
              alt="Neutral Fuels"
              class="relative h-28 w-28 rounded-full shadow-accent-glow ring-2 ring-white/10"
            />
          </div>
        </div>

        <h1 class="text-display text-white mb-4 text-balance">
          Powering a <span class="gradient-accent-text">Greener</span> Future
        </h1>
        <p class="text-lg text-primary-200 dark:text-surface-400 leading-relaxed">
          Track your sustainability journey, manage deliveries, and monitor your carbon impact — all in one place.
        </p>

        <!-- Decorative stats row -->
        <div class="mt-12 grid grid-cols-3 gap-4">
          <div class="rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 p-4">
            <div class="text-2xl font-extrabold text-accent-400">100%</div>
            <div class="text-xs text-primary-200 dark:text-surface-500 mt-1">Clean Energy</div>
          </div>
          <div class="rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 p-4">
            <div class="text-2xl font-extrabold text-accent-400">24/7</div>
            <div class="text-xs text-primary-200 dark:text-surface-500 mt-1">Portal Access</div>
          </div>
          <div class="rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 p-4">
            <div class="text-2xl font-extrabold text-accent-400">Live</div>
            <div class="text-xs text-primary-200 dark:text-surface-500 mt-1">Tracking</div>
          </div>
        </div>
      </div>
    </div>

    <!-- =============================================
         RIGHT PANEL — Login Form
         ============================================= -->
    <div class="flex-1 flex items-center justify-center relative bg-surface-50 dark:bg-surface-950 px-4 sm:px-6 lg:px-8 py-12">

      <!-- Mobile animated background (visible only on small screens) -->
      <div class="lg:hidden absolute inset-0 overflow-hidden">
        <div class="absolute inset-0 bg-primary-500 dark:bg-surface-950"></div>
        <div class="orb orb-1"></div>
        <div class="orb orb-2"></div>
        <div class="orb orb-3"></div>
      </div>

      <!-- Desktop subtle background texture -->
      <div class="hidden lg:block absolute inset-0 bg-dot-pattern dark:bg-dot-pattern-dark bg-dot-md opacity-40"></div>

      <div class="relative z-10 w-full max-w-md space-y-8 animate-fade-up">

        <!-- Mobile logo (hidden on desktop) -->
        <div class="lg:hidden text-center">
          <div class="flex justify-center mb-6">
            <div class="relative">
              <div class="absolute -inset-4 bg-accent-500/20 rounded-full blur-2xl animate-pulse-soft"></div>
              <img
                src="../../assets/earth.png"
                alt="Neutral Fuels"
                class="relative h-20 w-20 rounded-full shadow-accent-glow ring-2 ring-white/10"
              />
            </div>
          </div>
          <h1 class="text-display-sm text-white mb-2">Welcome Back</h1>
          <p class="text-primary-200 dark:text-surface-400">Sign in to your Customer Portal</p>
        </div>

        <!-- Form card -->
        <div class="card-glass lg:card lg:shadow-elevation-3 lg:dark:shadow-glass-lg p-8 lg:p-10 space-y-8">

          <!-- Desktop heading (hidden on mobile) -->
          <div class="hidden lg:block">
            <h2 class="text-display-sm text-surface-900 dark:text-surface-50 mb-2">Welcome back</h2>
            <p class="text-surface-500 dark:text-surface-400">Sign in to your Customer Portal</p>
          </div>

          <form @submit.prevent="handleLogin" class="space-y-6">
            <!-- Email Input -->
            <div>
              <label for="email" class="label">
                Email Address
              </label>
              <div class="relative group">
                <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <EnvelopeIcon class="h-5 w-5 text-surface-400 group-focus-within:text-accent-500 transition-colors duration-200" />
                </div>
                <input
                  id="email"
                  v-model="email"
                  type="email"
                  autocomplete="email"
                  required
                  class="input pl-12 py-3.5 text-base"
                  placeholder="you@example.com"
                  :disabled="isPending"
                />
              </div>
            </div>

            <!-- Password Input -->
            <div>
              <label for="password" class="label">
                Password
              </label>
              <div class="relative group">
                <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <LockClosedIcon class="h-5 w-5 text-surface-400 group-focus-within:text-accent-500 transition-colors duration-200" />
                </div>
                <input
                  id="password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  autocomplete="current-password"
                  required
                  class="input pl-12 pr-12 py-3.5 text-base"
                  placeholder="Enter your password"
                  :disabled="isPending"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute inset-y-0 right-0 pr-4 flex items-center text-surface-400 hover:text-surface-600 dark:hover:text-surface-300 transition-colors duration-200"
                  :disabled="isPending"
                >
                  <EyeIcon v-if="!showPassword" class="h-5 w-5" />
                  <EyeSlashIcon v-else class="h-5 w-5" />
                </button>
              </div>
            </div>

            <!-- Error Message -->
            <transition
              enter-active-class="transition duration-300 ease-spring"
              enter-from-class="opacity-0 -translate-y-2"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition duration-200 ease-in"
              leave-from-class="opacity-100 translate-y-0"
              leave-to-class="opacity-0 -translate-y-2"
            >
              <div
                v-if="error"
                class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl p-4 flex items-start space-x-3"
              >
                <ExclamationCircleIcon class="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p class="text-sm text-red-700 dark:text-red-300">{{ error }}</p>
              </div>
            </transition>

            <!-- Submit Button -->
            <BaseButton
              type="submit"
              variant="primary"
              size="lg"
              class="w-full"
              :loading="isPending"
              :disabled="!email || !password"
            >
              {{ isPending ? 'Signing in...' : 'Sign In' }}
            </BaseButton>

            <!-- Forgot Password Link -->
            <div class="flex items-center justify-between text-sm">
              <span class="text-surface-400 dark:text-surface-500">
                {{ versionNumber }}
              </span>
              <router-link
                to="/forgot-password"
                class="font-semibold text-accent-600 hover:text-accent-500 dark:text-accent-400 dark:hover:text-accent-300 transition-colors duration-200"
              >
                Forgot your password?
              </router-link>
            </div>
          </form>
        </div>

        <!-- Footer -->
        <div class="text-center text-sm text-surface-400 dark:text-surface-500 lg:text-surface-500 space-y-1">
          <p class="lg:hidden text-white/60">Powered by Neutral Fuels</p>
          <p class="hidden lg:block">Powered by <span class="font-semibold gradient-accent-text">Neutral Fuels</span></p>
          <p class="lg:hidden text-white/40">Need help? Contact your administrator</p>
          <p class="hidden lg:block text-surface-400 dark:text-surface-500">Need help? Contact your administrator</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import BaseButton from '@/components/ui/BaseButton.vue'
import {
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationCircleIcon
} from '@heroicons/vue/24/outline'

const store = useStore()
const router = useRouter()

const email = ref('')
const password = ref('')
const showPassword = ref(false)
const isPending = ref(false)
const error = ref(null)

const versionNumber = computed(() => store.state.versionNumber)

const handleLogin = async () => {
  error.value = null
  isPending.value = true

  try {
    await store.dispatch('login', {
      email: email.value,
      password: password.value,
    })

    // Watch for account type to determine redirect
    watch(
      () => store.state.userProfile?.account_type,
      (newVal) => {
        if (newVal) {
          if (newVal === 'CUSTOMER') {
            router.push('/dashboard')
          } else if (newVal === 'SUPPLIER') {
            router.push('/supplier/dashboard')
          }
        }
      },
      { immediate: true }
    )
  } catch (err) {
    isPending.value = false
    error.value = err.message || 'Invalid email or password. Please try again.'
  }
}
</script>

<style scoped>
/* ---- Animated Gradient Orbs ---- */
.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.5;
  will-change: transform;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.4) 0%, transparent 70%);
  top: -10%;
  left: -10%;
  animation: orbFloat1 14s ease-in-out infinite;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(6, 182, 212, 0.35) 0%, transparent 70%);
  bottom: -5%;
  right: -5%;
  animation: orbFloat2 18s ease-in-out infinite;
}

.orb-3 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(0, 58, 109, 0.3) 0%, transparent 70%);
  top: 40%;
  left: 30%;
  animation: orbFloat3 12s ease-in-out infinite;
}

.orb-4 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.25) 0%, transparent 70%);
  bottom: 20%;
  left: -5%;
  animation: orbFloat4 16s ease-in-out infinite;
}

@keyframes orbFloat1 {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  25% {
    transform: translate(60px, 40px) scale(1.1);
  }
  50% {
    transform: translate(20px, 80px) scale(0.95);
  }
  75% {
    transform: translate(-40px, 30px) scale(1.05);
  }
}

@keyframes orbFloat2 {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  25% {
    transform: translate(-50px, -30px) scale(1.05);
  }
  50% {
    transform: translate(-80px, 20px) scale(1.1);
  }
  75% {
    transform: translate(-20px, -60px) scale(0.95);
  }
}

@keyframes orbFloat3 {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(40px, -50px) scale(1.1);
  }
  66% {
    transform: translate(-30px, 40px) scale(0.9);
  }
}

@keyframes orbFloat4 {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  30% {
    transform: translate(50px, -40px) scale(1.05);
  }
  60% {
    transform: translate(-20px, -70px) scale(1.1);
  }
}

/* Dark mode orb adjustments */
:deep(.dark) .orb {
  opacity: 0.35;
}
</style>
